import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ModeloCardapio } from '@mobile/app/services/cardapio/cardapio.service';
import { arredondar, desmembrarCombo, precoDoCombo, precoDoProduto, saboresDoGrupo, totalEscolhasDoGrupo } from '@mobile/helpers/combo-promocao.helper';
import { descricaoVigencia } from '@mobile/helpers/vigencia.helper';
import { EscolhaCombo, OpcaoGrupoCombo, Produto } from '@mobile/models/cardapio/cardapio.model';

const MAX_ITENS_VISIVEIS = 4;

@Component({
  selector: 'app-combo-card',
  templateUrl: './combo-card.component.html',
  styleUrls: ['./combo-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboCardComponent implements OnChanges {

  @Input() combo!: Produto;
  @Input() modelo?: ModeloCardapio;
  @Output() abrir = new EventEmitter<Produto>();

  itens: string[] = [];
  itensExtras = 0;
  economia = 0;
  validade = '';

  get imagem(): string | undefined {
    return this.combo.imagens?.banner || this.combo.imagens?.imagem;
  }

  get aPartirDe(): boolean {
    return !!this.combo.grupos?.length;
  }

  ngOnChanges(): void {
    const nomes = this.combo.itensFixos?.map(i => `${i.quantidade}x ${this.nomeDe(i.produtoId)}`) ?? [];
    const escolhas = (this.combo.grupos ?? []).map(g => g.titulo);
    const todos = [...nomes, ...escolhas];

    this.itens = todos.slice(0, MAX_ITENS_VISIVEIS);
    this.itensExtras = Math.max(0, todos.length - MAX_ITENS_VISIVEIS);
    this.validade = descricaoVigencia(this.combo.vigencia);
    this.economia = this.calcularEconomia();
  }

  private nomeDe(produtoId: string): string {
    return this.modelo?.produtos.find(p => p.id === produtoId)?.nome ?? 'Item';
  }

  /** Economia mínima garantida: em cada grupo obrigatório considera a opção que menos economiza. */
  private calcularEconomia(): number {
    if (!this.modelo) {
      return 0;
    }
    const { produtos, tabelas, categorias } = this.modelo;

    const menorPreco = (opcoes: OpcaoGrupoCombo[], adicional: boolean): OpcaoGrupoCombo => {
      const vantagem = (o: OpcaoGrupoCombo): number => {
        const produto = produtos.find(p => p.id === o.produtoId);
        const preco = produto ? precoDoProduto(produto, tabelas, o.tamanhoId) : undefined;
        return (preco ?? 0) - (adicional ? o.valorAdicional ?? 0 : 0);
      };
      return opcoes.reduce((a, b) => (vantagem(b) < vantagem(a) ? b : a));
    };

    const escolhas: EscolhaCombo[] = (this.combo.grupos ?? [])
      .map(g => ({ g, opcoes: g.multiParte ? saboresDoGrupo(g, this.modelo!) : g.opcoes }))
      .filter(({ g, opcoes }) => g.obrigatorio && opcoes.length)
      .map(({ g, opcoes }) => ({
        grupoId: g.id,
        opcoes: Array.from({ length: totalEscolhasDoGrupo(g) }, () => menorPreco(opcoes, !g.multiParte)),
      }));

    const itensDesmembrados = desmembrarCombo(this.combo, escolhas, produtos, tabelas, categorias);
    const precoTabela = itensDesmembrados.reduce((soma, i) => soma + i.precoTabela * i.quantidade, 0);
    return Math.max(0, arredondar(precoTabela - precoDoCombo(this.combo, escolhas)));
  }
}
